import { LeadCaptureFormValues } from '@components/LeadCaptureForm/LeadCaptureForm.types';

const LEAD_RECORD_TYPE_ID = import.meta.env.VITE_LEAD_RECORD_TYPE_ID;
const LEAD_OWNER_ID = import.meta.env.VITE_LEAD_OWNER_ID;

export const INITIAL_LEAD_CAPTURE_VALUES: LeadCaptureFormValues = {
  customerType: 'RESIDENTIAL',
  firstName: '',
  lastName: '',
  company: '',
  preferredContactMethod: 'email',
  bestTimeToContact: '',
  email: '',
  phone: '',
  recordTypeId: LEAD_RECORD_TYPE_ID,
  status: 'New',
  ownerId: LEAD_OWNER_ID,
  typeOfServiceNeeded: [],
  howDidYouHear: null,
  otherLeadSource: '',
  messageDetails: '',
  streetNumber: '',
  streetName: '',
  unitNumber: '',
  city: '',
  state: null,
  postalCode: '',
  country: 'Canada',
  businessFields: {
    companyTradeName: '',
    industrySector: null,
    serviceOfInterest: [],
    numberOfEmployees: null,
  },
  onNet: null,
  qualificationStatus: '',
};

export const BUSINESS_TYPE_OPTIONS = [
  { value: 'retail', label: 'Retail' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance' },
  { value: 'education', label: 'Education' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'other', label: 'Other' },
];

/**
 * Slug -> Salesforce picklist API name. Wire-boundary mapping applied in
 * buildLeadSubmissionPayload; internal slugs stay stable and grep-friendly.
 */
export const BUSINESS_TYPE_CRM_VALUE: Record<string, string> = {
  retail: 'Retail',
  healthcare: 'Healthcare',
  technology: 'Technology',
  finance: 'Finance',
  education: 'Education',
  manufacturing: 'Manufacturing',
  hospitality: 'Hospitality',
  real_estate: 'Real Estate',
  professional_services: 'Professional Services',
  other: 'Other',
};

export const SERVICE_OPTIONS = [
  { value: 'Business Internet', label: 'Business Internet' },
  { value: 'Enterprise Solutions', label: 'Enterprise Solutions' },
  { value: 'Wholesale Solutions', label: 'Wholesale Solutions' },
  { value: 'Bulk/MDU Internet', label: 'Bulk/MDU Internet' },
  { value: 'Television', label: 'Television' },
  { value: 'Phone', label: 'Phone' },
  { value: 'Dark Fibre', label: 'Dark Fibre' },
  { value: 'Strata Solutions', label: 'Strata Solutions' },
  { value: 'Developer Solutions', label: 'Developer Solutions' },
  { value: 'Property Management', label: 'Property Management' },
  { value: 'Managed IT Solutions', label: 'Managed IT Solutions' },
];

export const PROVINCES_OPTIONS = [
  { value: 'alberta', label: 'Alberta' },
  { value: 'british_columbia', label: 'British Columbia' },
  { value: 'manitoba', label: 'Manitoba' },
  { value: 'new_brunswick', label: 'New Brunswick' },
  { value: 'newfoundland_and_labrador', label: 'Newfoundland and Labrador' },
  { value: 'nova_scotia', label: 'Nova Scotia' },
  { value: 'ontario', label: 'Ontario' },
  { value: 'prince_edward_island', label: 'Prince Edward Island' },
  { value: 'quebec', label: 'Quebec' },
  { value: 'saskatchewan', label: 'Saskatchewan' },
];

/** Maps Google Places short-name abbreviations to PROVINCES_OPTIONS values. */
export const PROVINCE_ABBREVIATION_MAP: Record<string, string> = {
  AB: 'alberta',
  BC: 'british_columbia',
  MB: 'manitoba',
  NB: 'new_brunswick',
  NL: 'newfoundland_and_labrador',
  NS: 'nova_scotia',
  ON: 'ontario',
  PE: 'prince_edward_island',
  QC: 'quebec',
  SK: 'saskatchewan',
  NT: 'northwest_territories',
  NU: 'nunavut',
  YT: 'yukon',
};

export const HEAR_ABOUT_OPTIONS = [
  { value: 'none', label: 'None' },
  {
    value: 'Referral from friend, family, co-worker, landlord etc',
    label: 'Referral from friend, family, co-worker, landlord etc',
  },
  { value: 'Searched online', label: 'Searched online' },
  { value: 'Saw an online ad', label: 'Saw an online ad' },
  {
    value: 'Outdoor Advertising - Billboards, transit, skytrain',
    label: 'Outdoor Advertising - Billboards, transit, skytrain',
  },
  { value: 'Postcards', label: 'Postcards' },
  { value: 'Returning Customer', label: 'Returning Customer' },
  { value: 'Online forum like Reddit', label: 'Online forum like Reddit' },
  { value: 'Asked ChatGPT', label: 'Asked ChatGPT' },
  { value: 'Social Media - Facebook, Instagram, X', label: 'Social Media - Facebook, Instagram, X' },
  { value: 'Saw Poster/Flyer/Brochure in building', label: 'Saw Poster/Flyer/Brochure in building' },
  { value: 'Sales Event in my building', label: 'Sales Event in my building' },
  { value: 'Saw Novus Vehicle', label: 'Saw Novus Vehicle' },
  { value: 'Visited/Saw the Store in Yaletown', label: 'Visited/Saw the Store in Yaletown' },
  { value: 'Other', label: 'Other' },
];

export const BUSINESS_SIZE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '50', label: '50' },
  { value: '200', label: '200' },
  { value: '500', label: '500' },
];

// ---------------------------------------------------------------------------
// Context messages shown at the top of the lead capture form
// ---------------------------------------------------------------------------

export const CONTEXT_MESSAGES = {
  BUSINESS:
    'Good news, your address may be serviceable. Please complete the form below so our team can review your request and follow up to confirm availability and next steps.',
  PLANNED:
    'Looks like your building is on our to-do list and is in the planning stages. Please fill out the form below so we can add you to our list of interested customers who want our service.',
  CONSTRUCTION:
    "Congrats! Our techs are busy wiring up your building. Please fill out the form below so we can let you know when it's time to switch over to Novus!",
  ADDRESS_NOT_FOUND:
    "We need your help! It looks like we're not in your building yet. Please provide us with more information so we can add your building to our wish list and keep you posted on progress status changes.",
  NON_BOOKABLE:
    'Good news, your address may be serviceable. Please complete the form below so our team can review your request and follow up to confirm availability and next steps.',
  CONTACT_US:
    "We'd love to help you get set up and finish your order. Please fill out the form below and someone from our team will get back to you within 24 hours.",
} as const;
